import { create } from 'zustand';

import {
  acceptFriendRequest,
  blockUser,
  getFriendRequests,
  getFriends,
  rejectFriendRequest,
  removeFriend,
  searchUsers,
  sendFriendRequest,
  type AccountSearchResultJson,
  type FriendInfoJson,
  type FriendRequestJson,
} from '../platform/api';
import { useToasts } from '../toasts';

export interface FriendsState {
  friends: FriendInfoJson[];
  pendingIncoming: FriendRequestJson[];
  pendingOutgoing: FriendRequestJson[];
  loading: boolean;
  loadFriends(): Promise<void>;
  loadRequests(): Promise<void>;
  searchUsers(query: string): Promise<AccountSearchResultJson[]>;
  sendRequest(toId: number): Promise<void>;
  acceptRequest(fromId: number): Promise<void>;
  rejectRequest(fromId: number): Promise<void>;
  removeFriend(friendId: number): Promise<void>;
  blockUser(userId: number): Promise<void>;
}

export const useFriends = create<FriendsState>((set, get) => ({
  friends: [],
  pendingIncoming: [],
  pendingOutgoing: [],
  loading: false,

  async loadFriends() {
    try {
      const friends = await getFriends();
      set({ friends });
    } catch (e) {
      useToasts.getState().push('error', `Failed to load friends: ${e instanceof Error ? e.message : String(e)}`);
    }
  },

  async loadRequests() {
    try {
      const data = await getFriendRequests();
      set({ pendingIncoming: data.incoming, pendingOutgoing: data.outgoing });
    } catch {
      // Non-critical — friend requests panel still renders with empty lists
    }
  },

  async searchUsers(query: string) {
    try {
      return await searchUsers(query);
    } catch {
      return [];
    }
  },

  async sendRequest(toId: number) {
    try {
      await sendFriendRequest(toId);
      useToasts.getState().push('success', 'Friend request sent');
      void get().loadRequests();
    } catch (e) {
      useToasts.getState().push('error', `Failed to send request: ${e instanceof Error ? e.message : String(e)}`);
    }
  },

  async acceptRequest(fromId: number) {
    try {
      await acceptFriendRequest(fromId);
      useToasts.getState().push('success', 'Friend request accepted');
      void get().loadFriends();
      void get().loadRequests();
    } catch (e) {
      useToasts.getState().push('error', `Failed to accept: ${e instanceof Error ? e.message : String(e)}`);
    }
  },

  async rejectRequest(fromId: number) {
    try {
      await rejectFriendRequest(fromId);
      void get().loadRequests();
    } catch (e) {
      useToasts.getState().push('error', `Failed to reject: ${e instanceof Error ? e.message : String(e)}`);
    }
  },

  async removeFriend(friendId: number) {
    try {
      await removeFriend(friendId);
      set((s) => ({ friends: s.friends.filter((f) => f.account_id !== friendId) }));
    } catch (e) {
      useToasts.getState().push('error', `Failed to remove friend: ${e instanceof Error ? e.message : String(e)}`);
    }
  },

  async blockUser(userId: number) {
    try {
      await blockUser(userId);
      set((s) => ({
        friends: s.friends.filter((f) => f.account_id !== userId),
        pendingIncoming: s.pendingIncoming.filter((r) => r.from_id !== userId),
        pendingOutgoing: s.pendingOutgoing.filter((r) => r.to_id !== userId),
      }));
      useToasts.getState().push('success', 'User blocked');
    } catch (e) {
      useToasts.getState().push('error', `Failed to block: ${e instanceof Error ? e.message : String(e)}`);
    }
  },
}));
